import React, { useState, useEffect, useContext, useRef, forwardRef } from 'react';
import { AppContext } from '../../../util/AppContext';
import PropType from 'prop-types';
import { getConfigValue } from '../../../util/getConfigValue';
import { Chart as ChartJS, Title, Legend, ArcElement } from 'chart.js';
import { Doughnut } from 'react-chartjs-2';
import { downloadDivChart, getTimePeriodLabel, utilizationReportConstants, standardizeChartNames, standardizeChartDataLabels, getTimePeriodTile, baseDoughnutChartOptions, beforeDrawHandler } from './utlizationChart';
import { getProductsByMessageType } from '../../../API/UtilizationReportingDashboard';
import { useErrorBoundary } from "react-error-boundary";
import ReportInfoTooltip from "../ReportInfoTooltip";
import Button from 'react-bootstrap/Button';
import ChartDataLabels from "chartjs-plugin-datalabels";
import Mustache from 'mustache';


PdfGenerationsByMessageTypeChart.propTypes = {
    filterState: PropType.object,
    colorRegistry: PropType.object
}

function PdfGenerationsByMessageTypeChart(props) {
    const {
        filterState,
        colorRegistry
    } = props;

    const { showBoundary } = useErrorBoundary();
    const [appState] = useContext(AppContext);
    const doughnutChartRefs = useRef([]);
    const [data, setData] = useState({});
    const [doughnutChartData, setDoughnutChartData] = useState({});
    const [isLoading, setIsLoading] = useState(null);
    const [isCollapsed, setIsCollapsed] = useState(false);
    const [activeButton, setActiveButton] = useState(utilizationReportConstants.current);
    const tooltipTarget = useRef(null);
    const chartEnabled = JSON.parse(getConfigValue(appState?.config?.UtilizationDashboard?.Charts?.PdfGenerationsByMessageType?.Enabled, appState)?.Value === 'true') || false;
    const [noData, setNoData] = useState(null);

    ChartJS.register(Title, Legend, ArcElement, ChartDataLabels);

    const DoughnutChart = forwardRef(({ productData, columnSize }, chartRef) => {
        const chartData = {
            labels: productData.chartData.labels,
            datasets: productData.chartData.dataSets
        }

        const doughnutChartOptions = {
            type: "doughnut",
            responsive: true,
            maintainAspectRatio: false,
            resizeDelay:  200,
            layout: baseDoughnutChartOptions.layout,
            plugins:
            {
                datalabels: {
                    ...baseDoughnutChartOptions.datalabels,
                    formatter: function (value, context) {
                        return `${(value / context.dataset.totals[0] * 100).toFixed(0)}%`;
                    }  
                },                  
                legend: baseDoughnutChartOptions.legend,
                tooltip: {
                    ...baseDoughnutChartOptions.tooltip,
                    callbacks: {
                        label: function (tooltipItem) {
                            return ` ${tooltipItem.raw} / ${(tooltipItem.raw / tooltipItem.dataset.totals[0] * 100).toFixed(0)}%`;
                        }
                    }
                },
                title: baseDoughnutChartOptions.title,
                centerTextPlugin: {
                    text: productData.name
                },
            },
        };

        const colors = chartData.datasets[0].backgroundColor;
        return (
            <div className={`col-${columnSize} chart-canvas-wrapper mb-5`}>
                <Doughnut ref={chartRef} options={doughnutChartOptions} data={chartData} />
                <div className='chart-legend mt-2 d-flex flex-wrap justify-content-center gap-1'>
                    {chartData.labels.map((label, index) => (
                        <div key={index} className='d-flex align-items-center gap-1 color-grey-d1'>
                            <span
                            style= {{
                                width: '40px',
                                height: '18px',
                                backgroundColor: colors[index],
                                borderRadius: '2px',
                                display: 'inline-block'
                            }} />
                            <span style={{fontSize: '20px'}}>{label}</span>
                        </div>
                    ))}
                </div>
            </div>
        )
    });

    ChartJS.register({
        id: "centerTextPlugin",
        beforeDraw: beforeDrawHandler
    });


    const getData = async () => {
        if (filterState == undefined || filterState?.selectedProductIds == null) {
            return;
        }

        setIsLoading(true);
        await getProductsByMessageType({
            "timeFilter": filterState.timePeriodId,
            "productIds": filterState.selectedProductIds,
            "geography": filterState.selectedGeography,
            "geographyType": filterState.selectedGeographyType,
            "includePriorPeriod": true
        })
            .then((responseJson) => {
                setData(responseJson);
                setChartData(responseJson.currentPeriod);
            })
            .catch((err) => {
                showBoundary(err);
            })
            .finally(() => {
                setIsLoading(false);
            });
    }

    const toggleChartTab = (activeTab, selectedData) => {
        setChartData(selectedData);
        setActiveButton(activeTab);
    }

    const setChartData = (selectedData) => {
        let filteredDataSet = filterState.allProducts
            ? selectedData.filter(p => !p.chartData.dataSets.some(d => d.label !== utilizationReportConstants.allProducts))
            : selectedData.filter(p => !p.chartData.dataSets.some(d => d.label === utilizationReportConstants.allProducts));

        setNoData(filteredDataSet.filter(p => p.chartData.labels.some(d => d === utilizationReportConstants.no_data)));
        filteredDataSet = filteredDataSet.filter(p => !p.chartData.labels.some(d => d === utilizationReportConstants.no_data));

        let standardizedData = standardizeChartNames(filteredDataSet, appState);
        standardizedData = standardizeChartDataLabels(standardizedData, appState);

        const data = standardizedData.map(item => ({
            ...item,
            chartData: {
                ...item.chartData,
                dataSets: item.chartData.dataSets.map(ds =>
                    addColorPalette(ds, item.chartData.labels),
                )
            }
        }));

        setDoughnutChartData(data);

    }

    const addColorPalette = (selectedDataSet, labels = []) => {
        const assignedColorPallet = labels.map(key => colorRegistry.getColorForKey(key));
        return {
            ...selectedDataSet,
            backgroundColor: assignedColorPallet
        };
    }

    useEffect(() => {
        if (chartEnabled && appState?.config) {
            getData();
            setActiveButton(utilizationReportConstants.current);
        }
    }, [filterState.selectedProductIds, filterState.timePeriodId, filterState.selectedGeography]);

    let content;
    if (isLoading === null || isLoading) {
        content = (<div className='p-5'>{getConfigValue(appState.config.Common?.Loading, appState)?.Value}</div>);
    }
    else {
        content = (
            <>
                <div id="pdfGenerationsByMessageType-ChartsArea">
                    <div className='p-4 chart-label'>
                        <div>{getTimePeriodLabel(filterState.timePeriodId, activeButton, false)}</div>
                        <div>{filterState.allProducts ? Mustache.render(getConfigValue(appState?.config?.UtilizationDashboard?.Filters?.Product?.AllProducts?.Label, appState)?.Value || null, { count: appState?.sellSheetProductDetails?.length }) : filterState.selectedProductIds?.map(id => {
                            const product = appState?.sellSheetProductDetails.find(p => p.clientProductId === id);
                            return product.name || `Unknow (${id})`;
                        }).join(', ')}</div>
                        <div>{!filterState.selectedGeography ? getConfigValue(appState?.config?.UtilizationDashboard?.Filters?.Product?.AllGeographies?.Label, appState)?.Value || null : filterState.selectedGeographyLabel}</div>
                    </div>
                    <div className='container-fluid'>
                        {Array.from({ length: Math.ceil(doughnutChartData.length / 2) }, (_, i) => {
                            return (<div className='row mb-5 justify-content-center align-items-center' key={i}>
                                {doughnutChartData.slice(i * 2, (i + 1) * 2).map((product, columnIndex) => {
                                    const refIndex = i * 2 + columnIndex;
                                    if (!doughnutChartRefs?.current[refIndex]) {
                                        doughnutChartRefs.current[refIndex] = React.createRef();
                                    }
                                    return (
                                        <DoughnutChart
                                            ref={doughnutChartRefs.current[refIndex]}
                                            columnSize = {6}
                                            productData={product}
                                            key={refIndex} />
                                    )
                                })}
                            </div>)
                        })}
                    </div>
                    <div className='p-4 chart-label'>
                        {(() => {
                            const names = noData.map(d => d.name);
                            const count = names.length;

                            const labelText = names.reduce((acc, curr, i, arr) => {
                                if (i === 0) return curr;
                                if (i === arr.length - 1) return `${acc}, and ${curr}`;
                                return `${acc}, ${curr}`;
                            }, '');

                            const verb = count === 1 ? 'has' : 'have';
                            if(names.length > 0) return `${labelText} ${verb} no data.`;
                        })()}
                    </div>
                </div>
                <div className='reportButtons-container'>

                    <Button
                        className='reportTextButton'
                        onClick={() => downloadDivChart('pdfGenerationsByMessageType-ChartsArea', getConfigValue(appState?.config?.UtilizationDashboard?.Charts?.PdfGenerationsByMessageType?.Title, appState)?.Value || null)}
                    >
                        {getConfigValue(appState?.config?.UtilizationDashboard?.Charts?.Buttons?.Download?.Text, appState)?.Value || null} <i className={getConfigValue(appState?.config?.UtilizationDashboard?.Charts?.Buttons?.Download?.Icon, appState)?.Value || null}></i>
                    </Button>
                </div>
                <div className='reportTabButton-container'>
                    <Button
                        className={`tab-button ${activeButton === utilizationReportConstants.current ? "active" : "inactive"}`}
                        onClick={() => { toggleChartTab(utilizationReportConstants.current, data.currentPeriod); }}
                    >
                        {getConfigValue(appState?.config?.UtilizationDashboard?.Charts?.Buttons?.CurrentSelection?.Text, appState)?.Value || null} ({getTimePeriodTile(filterState.timePeriodId, filterState.timePeriodTitle, false)})
                    </Button>
                    <Button
                        className={`tab-button ${activeButton === utilizationReportConstants.prior ? "active" : "inactive"}`}
                        onClick={() => { toggleChartTab(utilizationReportConstants.prior, data.priorPeriod); }}
                    >
                        {getConfigValue(appState?.config?.UtilizationDashboard?.Charts?.Buttons?.PriorTimePeriod?.Text, appState)?.Value || null}
                    </Button>
                </div>
            </>
        );
    }

    return (
        <>
            {chartEnabled &&
                <div className='chart-panel'>
                    <div className='chart-header'>
                        <Button className={`chart-header-button ${isCollapsed ? 'collapsed' : ''}`} onClick={() => { setIsCollapsed(!isCollapsed) }}>
                            {getConfigValue(appState?.config?.UtilizationDashboard?.Charts?.PdfGenerationsByMessageType?.Title, appState)?.Value || "Product by Message Types"}
                            <ReportInfoTooltip ref={tooltipTarget} text={getConfigValue(appState?.config?.UtilizationDashboard?.Charts?.PdfGenerationsByMessageType?.ToolTip, appState)?.Value} />
                        </Button>
                    </div>
                    <div className={`chart-container ${isCollapsed ? 'collapsed' : ''}`}>
                        {content}
                    </div>
                </div>
            }
        </>
    );
}

export default PdfGenerationsByMessageTypeChart;